import React, { useState, useEffect, useContext } from 'react';
import { AppContext } from '../context/AppContext';
import { useTranslation } from 'react-i18next';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import InvoiceReport from '../components/InvoiceReport';
import { useNavigate } from 'react-router-dom';

const statusColors = {
    Paid: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    Partial: 'bg-orange-100 text-orange-800 border-orange-200',
    Unpaid: 'bg-red-100 text-red-800 border-red-200',
    Refunded: 'bg-purple-100 text-purple-800 border-purple-200'
};

const getLocalDate = () => {
    const d = new Date();
    const offset = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - offset).toISOString().split('T')[0];
};

const SalesTransactions = () => {
    const { authFetch, depos, selectedDepo, user } = useContext(AppContext);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [invoices, setInvoices] = useState([]);
    
    // Filters
    const [filters, setFilters] = useState({
        invoice_number: '',
        start_date: getLocalDate(),
        end_date: getLocalDate(),
        cashier_id: 'All',
        payment_method: 'All',
        customer_id: 'All',
        depo_id: selectedDepo,
        status: 'All'
    });

    const [users, setUsers] = useState([]);
    const [customers, setCustomers] = useState([]);

    const [isPrintOpen, setIsPrintOpen] = useState(false);
    const [isConvertOpen, setIsConvertOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    
    // Convert payment state
    const [convertMethod, setConvertMethod] = useState('');
    const [currentMethod, setCurrentMethod] = useState('');

    // Edit state
    const [editItems, setEditItems] = useState([]);
    const [editNote, setEditNote] = useState('');
    const [editReason, setEditReason] = useState('');
    const [prodSearch, setProdSearch] = useState('');
    const [products, setProducts] = useState([]);

    useEffect(() => {
        authFetch('/api/users').then(r => r.json()).then(d => setUsers(Array.isArray(d) ? d : []));
        authFetch('/api/customers').then(r => r.json()).then(d => setCustomers(Array.isArray(d) ? d : []));
        authFetch('/api/products').then(r => r.json()).then(d => setProducts(Array.isArray(d) ? d : [])).catch(() => {});
    }, [authFetch]);

    useEffect(() => {
        fetchAll();
    }, [filters, authFetch]);

    const fetchAll = () => {
        let url = '/api/invoices';
        const params = new URLSearchParams();
        if (filters.invoice_number) params.append('invoice_number', filters.invoice_number);
        if (filters.start_date) params.append('start_date', filters.start_date);
        if (filters.end_date) params.append('end_date', filters.end_date);
        if (filters.cashier_id !== 'All') params.append('cashier_id', filters.cashier_id);
        if (filters.payment_method !== 'All') params.append('payment_method', filters.payment_method);
        if (filters.customer_id !== 'All') params.append('customer_id', filters.customer_id);
        if (filters.depo_id !== 'Global') params.append('depo_id', filters.depo_id);
        if (filters.status !== 'All') params.append('status', filters.status);
        
        if (params.toString()) url += `?${params.toString()}`;

        authFetch(url).then(r => r.json()).then(d => setInvoices(Array.isArray(d) ? d : []));
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleConvert = async (e) => {
        e.preventDefault();
        if (!convertMethod || convertMethod === currentMethod) {
            alert(t('Please select a different payment method to convert to.'));
            return;
        }

        try {
            const res = await authFetch('/api/convert_payment', {
                method: 'POST',
                body: JSON.stringify({
                    invoice_id: selectedInvoice.id,
                    old_method: currentMethod,
                    new_method: convertMethod
                })
            });
            const data = await res.json();
            if (res.ok) {
                alert(t('Payment converted successfully'));
                setIsConvertOpen(false);
                fetchAll();
            } else {
                alert(data.message || t('Error converting payment'));
            }
        } catch (err) {
            alert(t('Network error'));
        }
    };

    const handleEditSave = async (e) => {
        e.preventDefault();
        if (!editReason.trim()) { alert(t('Please enter a reason for the edit.')); return; }
        try {
            const res = await authFetch(`/api/sales/${selectedInvoice.id}`, {
                method: 'PUT',
                body: JSON.stringify({ items: editItems, note: editNote, reason: editReason })
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.message);
            setIsEditOpen(false);
            setSelectedInvoice(null);
            alert(t('Invoice updated and inventory synced'));
            fetchAll();
        } catch (err) {
            alert(t('Failed to save') + ': ' + err.message);
        }
    };

    const columns = [
        { header: t('Invoice No'), accessor: 'invoice_number', render: r => <span className="font-mono text-gray-500 font-bold">{r.invoice_number}</span> },
        { header: t('Date'), accessor: 'created_at', render: r => new Date(r.created_at).toLocaleString() },
        { header: t('Customer'), accessor: 'customer_name', render: r => r.customer_name || t('Walk-in Customer') },
        { header: t('Cashier'), accessor: 'cashier_name', render: r => (
            <div>
                <div className="font-bold text-gray-800">{r.cashier_name || 'System'}</div>
                {r.cashier_role && <div className="text-[10px] text-gray-500 uppercase">{r.cashier_role}</div>}
            </div>
        ) },
        { header: t('Payment Type'), accessor: 'payment_methods', render: r => (
            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                {r.payment_methods || (r.type === 'CREDIT' ? 'Account' : 'Unknown')}
            </span>
        )},
        { header: t('Total'), accessor: 'total_amount', render: r => `CFA${parseFloat(r.total_amount).toFixed(2)}` },
        { header: t('Discount'), accessor: 'discount', render: r => `CFA${parseFloat(r.discount || 0).toFixed(2)}` },
        { header: t('Tax'), accessor: 'tax', render: r => `CFA${parseFloat(r.tva || 0).toFixed(2)}` },
        { header: t('Final Amount'), accessor: 'total_ttc', render: r => <span className="font-bold text-indigo-700">CFA{parseFloat(r.total_ttc).toFixed(2)}</span> },
        {
            header: t('Status'), accessor: 'status', render: r => (
                <span className={`px-2 py-1 rounded-full text-xs font-bold border ${statusColors[r.status] || 'bg-gray-100'}`}>{t(r.status)}</span>
            )
        },
        {
            header: t('Actions'), accessor: 'actions', render: r => (
                <div className="flex gap-2">
                    {/* View */}
                    <button onClick={() => { setSelectedInvoice(r); setIsPrintOpen(true); }}
                        className="text-blue-500 hover:text-blue-700 bg-blue-50 p-2 rounded-full transition-colors" title={t('View')}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                    </button>
                    {/* Edit */}
                    <button onClick={() => { 
                            setSelectedInvoice(r); 
                            setEditItems(r.items ? r.items.map(it => ({ ...it, _new: false })) : []);
                            setEditNote(r.note || '');
                            setEditReason('');
                            setProdSearch('');
                            setIsEditOpen(true); 
                        }}
                        className="text-amber-500 hover:text-amber-700 bg-amber-50 p-2 rounded-full transition-colors" title={t('Edit')}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                    </button>
                    {/* Convert */}
                    {r.status === 'Paid' && (
                        <button onClick={() => { 
                                setSelectedInvoice(r); 
                                const pMethods = r.payment_methods ? r.payment_methods.split(', ') : ['Cash'];
                                setCurrentMethod(pMethods[0]);
                                setConvertMethod('');
                                setIsConvertOpen(true); 
                            }}
                            className="text-purple-500 hover:text-purple-700 bg-purple-50 p-2 rounded-full transition-colors" title={t('Convert Payment')}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                            </svg>
                        </button>
                    )}
                    {/* Return */}
                    {r.status === 'Paid' && (
                        <button onClick={() => navigate(`/pos?mode=return&invoice_id=${r.id}`)}
                            className="text-red-500 hover:text-red-700 bg-red-50 p-2 rounded-full transition-colors" title={t('Return Items')}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                            </svg>
                        </button>
                    )}
                </div>
            )
        },
    ];

    return (
        <div className="space-y-6">
            <header>
                <h2 className="text-3xl font-bold text-gray-800">{t('Sales Transactions')}</h2>
                <p className="text-gray-500 mt-1">{t('View, edit, and convert completed invoices.')}</p>
            </header>

            {/* Filter Section */}
            <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm grid grid-cols-1 md:grid-cols-4 lg:grid-cols-7 gap-4 items-end">
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Invoice Number')}</label>
                    <input type="text" value={filters.invoice_number} onChange={e => handleFilterChange('invoice_number', e.target.value)} className="w-full border p-2 rounded text-sm" placeholder="e.g. INV-1001" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Start Date')}</label>
                    <input type="date" value={filters.start_date} onChange={e => handleFilterChange('start_date', e.target.value)} className="w-full border p-2 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('End Date')}</label>
                    <input type="date" value={filters.end_date} onChange={e => handleFilterChange('end_date', e.target.value)} className="w-full border p-2 rounded text-sm" />
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Cashier')}</label>
                    <select value={filters.cashier_id} onChange={e => handleFilterChange('cashier_id', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Cashiers')}</option>
                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Payment Method')}</label>
                    <select value={filters.payment_method} onChange={e => handleFilterChange('payment_method', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Methods')}</option>
                        <option value="Cash">Cash</option>
                        <option value="Visa">Card / Visa</option>
                        <option value="Account">Account Credit</option>
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Customer')}</label>
                    <select value={filters.customer_id} onChange={e => handleFilterChange('customer_id', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Customers')}</option>
                        {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>
                <div>
                    <label className="block text-xs font-semibold text-gray-500 mb-1">{t('Status')}</label>
                    <select value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} className="w-full border p-2 rounded text-sm">
                        <option value="All">{t('All Status')}</option>
                        <option value="Paid">{t('Paid')}</option>
                        <option value="Partial">{t('Partial')}</option>
                        <option value="Unpaid">{t('Unpaid')}</option>
                        <option value="Refunded">{t('Refunded')}</option>
                    </select>
                </div>
            </div>

            <DataTable columns={columns} data={invoices} />

            {/* View Modal */}
            {isPrintOpen && selectedInvoice && (
                <InvoiceReport invoice={selectedInvoice} onClose={() => setIsPrintOpen(false)} />
            )}

            {/* Convert Payment Modal */}
            <Modal isOpen={isConvertOpen} onClose={() => setIsConvertOpen(false)} title={t('Convert Payment')}>
                <form onSubmit={handleConvert} className="space-y-4">
                    <p className="text-gray-600 text-sm">{t('Convert payment method for invoice')} <span className="font-bold text-gray-900">{selectedInvoice?.invoice_number}</span>.</p>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('Current Payment Method')}</label>
                        <input type="text" readOnly value={currentMethod} className="mt-1 w-full border bg-gray-100 p-2 rounded text-sm" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">{t('New Payment Method')}</label>
                        <select required value={convertMethod} onChange={e => setConvertMethod(e.target.value)} className="mt-1 w-full border p-2 rounded text-sm">
                            <option value="">{t('Select new method...')}</option>
                            {currentMethod !== 'Cash' && <option value="Cash">Cash</option>}
                            {currentMethod !== 'Visa' && <option value="Visa">Card / Visa</option>}
                            {currentMethod !== 'Account' && <option value="Account">Account Credit</option>}
                        </select>
                    </div>
                    <div className="flex justify-end gap-2 pt-4">
                        <button type="button" onClick={() => setIsConvertOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">{t('Cancel')}</button>
                        <button type="submit" className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">{t('Convert')}</button>
                    </div>
                </form>
            </Modal>

            {/* Edit Modal Placeholder -> Actual Edit Modal */}
            {isEditOpen && selectedInvoice && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={e => { if (e.target===e.currentTarget) setIsEditOpen(false); }}>
                    <div style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth:700, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,.3)' }}>
                        <div style={{ background:'linear-gradient(90deg,#1e1b4b,#312e81)', padding:'20px 24px', borderRadius:'16px 16px 0 0', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                            <div>
                                <div style={{ color:'#a5b4fc', fontSize:11, fontWeight:600 }}>EDIT INVOICE</div>
                                <div style={{ color:'#fff', fontWeight:900, fontSize:17 }}>{selectedInvoice.invoice_number}</div>
                            </div>
                            <button onClick={() => setIsEditOpen(false)} style={{ background:'rgba(255,255,255,.15)', color:'#fff', border:'none', borderRadius:8, width:32, height:32, fontSize:18, cursor:'pointer' }}>×</button>
                        </div>

                        <form onSubmit={handleEditSave} style={{ padding:24 }}>
                            <div style={{ fontWeight:700, fontSize:13, color:'#64748b', marginBottom:12, textTransform:'uppercase', letterSpacing:'.5px' }}>Line Items</div>
                            {editItems.map((item, i) => (
                                <div key={i} style={{ display:'flex', gap:8, marginBottom:10, alignItems:'center', background:'#f8fafc', borderRadius:10, padding:'10px 14px', flexWrap:'wrap' }}>
                                    <div style={{ flex:2, minWidth:140, fontSize:13, fontWeight:600, color:'#0f172a' }}>{item.product_name}</div>
                                    <div style={{ display:'flex', gap:8, flex:3, minWidth:200, flexWrap:'wrap' }}>
                                        <div style={{ flex:1, minWidth:80 }}>
                                            <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>QTY</label>
                                            <input type="number" min="0.01" step="0.01" required value={item.quantity}
                                                onChange={e => { const ni=[...editItems]; ni[i]={...ni[i],quantity:e.target.value}; setEditItems(ni); }}
                                                style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                                            />
                                        </div>
                                        <div style={{ flex:1, minWidth:80 }}>
                                            <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>PRICE</label>
                                            <input type="number" min="0" step="0.01" required value={item.unit_price}
                                                onChange={e => { const ni=[...editItems]; ni[i]={...ni[i],unit_price:e.target.value}; setEditItems(ni); }}
                                                style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                                            />
                                        </div>
                                        <div style={{ flex:1, minWidth:80 }}>
                                            <label style={{ fontSize:10, color:'#94a3b8', display:'block', marginBottom:3 }}>DISCOUNT</label>
                                            <input type="number" min="0" step="0.01" value={item.discount||0}
                                                onChange={e => { const ni=[...editItems]; ni[i]={...ni[i],discount:e.target.value}; setEditItems(ni); }}
                                                style={{ width:'100%', padding:'7px 10px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                                            />
                                        </div>
                                    </div>
                                    <button type="button" onClick={() => setEditItems(editItems.filter((_, idx)=>idx!==i))} style={{ background:'#fee2e2', color:'#ef4444', border:'none', padding:'8px 12px', borderRadius:8, cursor:'pointer', fontWeight:700 }}>🗑</button>
                                </div>
                            ))}

                            <div style={{ position:'relative', marginTop:20 }}>
                                <input placeholder="Type product name to add..." value={prodSearch} onChange={e=>setProdSearch(e.target.value)} style={{ width:'100%', padding:'12px 16px', borderRadius:10, border:'2px dashed #cbd5e1', fontSize:13, outline:'none', boxSizing:'border-box' }} />
                                {prodSearch && (
                                    <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, maxHeight:200, overflowY:'auto', zIndex:10, boxShadow:'0 10px 15px -3px rgba(0,0,0,0.1)' }}>
                                        {products.filter(p=>p.name.toLowerCase().includes(prodSearch.toLowerCase()) || (p.barcode&&p.barcode.includes(prodSearch))).map(p => (
                                            <div key={p.id} onClick={() => {
                                                setEditItems([...editItems, { product_id: p.id, product_name: p.name, quantity: 1, unit_price: p.price, discount: 0, _new: true }]);
                                                setProdSearch('');
                                            }} style={{ padding:'10px 14px', borderBottom:'1px solid #f1f5f9', cursor:'pointer', fontSize:13, fontWeight:600 }}>
                                                {p.name} <span style={{ color:'#10b981', marginLeft:8 }}>CFA {p.price}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div style={{ marginTop:24 }}>
                                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:6 }}>Reason for Edit (Required for Audit)</label>
                                <textarea required value={editReason} onChange={e=>setEditReason(e.target.value)} style={{ width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', fontSize:13, minHeight:60, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }} placeholder="e.g. Wrong quantity entered..."></textarea>
                            </div>

                            <div style={{ marginTop:16 }}>
                                <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:6 }}>Private Note</label>
                                <textarea value={editNote} onChange={e=>setEditNote(e.target.value)} style={{ width:'100%', padding:'10px 14px', borderRadius:10, border:'1.5px solid #e2e8f0', fontSize:13, minHeight:60, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }} placeholder="Optional..."></textarea>
                            </div>

                            <div style={{ display:'flex', justifyContent:'flex-end', gap:12, marginTop:24 }}>
                                <button type="button" onClick={() => setIsEditOpen(false)} style={{ padding:'10px 20px', borderRadius:10, border:'none', background:'#f1f5f9', color:'#475569', fontWeight:700, cursor:'pointer' }}>Cancel</button>
                                <button type="submit" style={{ padding:'10px 20px', borderRadius:10, border:'none', background:'#4f46e5', color:'#fff', fontWeight:700, cursor:'pointer', boxShadow:'0 4px 12px rgba(79,70,229,.3)' }}>Save & Sync Inventory</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SalesTransactions;
